import { useState, useCallback, useRef, useEffect } from "react";

import { ReactComponent as ArrowIcon } from "assets/svgs/Arrow.svg";

const DEFAULT_MIN_SIZE = 140;
const DEFAULT_MAX_SIZE = 220;
const DEFAULT_VMIN_RATIO = 0.22; // 22vmin
/** Stick diameter as a fraction of pad size (matches former 80/180). */
const STICK_RATIO = 80 / 180;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const computeViewportSize = (minSize, maxSize, vminRatio) => {
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const vmin = Math.min(vw, vh);
    return Math.round(clamp(vmin * vminRatio, minSize, maxSize));
};

/**
 * Pad size that tracks viewport (clamp(min, vminRatio * vmin, max)).
 * Uses visualViewport when available so mobile browser chrome is accounted for.
 */
const useViewportJoystickSize = (minSize, maxSize, vminRatio) => {
    const [size, setSize] = useState(() =>
        typeof window === "undefined"
            ? minSize
            : computeViewportSize(minSize, maxSize, vminRatio)
    );

    useEffect(() => {
        const update = () => setSize(computeViewportSize(minSize, maxSize, vminRatio));
        update();
        window.addEventListener("resize", update);
        window.visualViewport?.addEventListener("resize", update);
        window.visualViewport?.addEventListener("scroll", update);
        return () => {
            window.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("scroll", update);
        };
    }, [minSize, maxSize, vminRatio]);

    return size;
};

/**
 * Joystick with a large hit zone: touch anywhere in the zone and the pad
 * center snaps under the finger (starts at 0 deflection). Drag relative to
 * that origin; on release the pad returns home.
 *
 * Size scales with viewport: clamp(minSize, vminRatio * vmin, maxSize).
 * Move events match react-joystick-component: x/y in roughly -1..1,
 * with screen-down as negative y.
 */
const JoystickWrapper = ({
    minSize = DEFAULT_MIN_SIZE,
    maxSize = DEFAULT_MAX_SIZE,
    vminRatio = DEFAULT_VMIN_RATIO,
    horizontalOnly = false,
    throttle = 50,
    handleMove = () => {},
    handleStop = () => {},
    className = "",
}) => {
    const size = useViewportJoystickSize(minSize, maxSize, vminRatio);
    const stickSize = Math.round(size * STICK_RATIO);
    const baseSize = size - 12;
    const radius = baseSize / 2;
    // Hit zone is 20% larger than the visual pad so nearby touches still grab control
    const hitZoneSize = size * 1.2;
    const homeCenter = hitZoneSize / 2;

    const zoneRef = useRef(null);
    const pointerIdRef = useRef(null);
    const originRef = useRef(null);
    const lastMoveAtRef = useRef(0);

    const handleMoveRef = useRef(handleMove);
    const handleStopRef = useRef(handleStop);
    useEffect(() => {
        handleMoveRef.current = handleMove;
    }, [handleMove]);
    useEffect(() => {
        handleStopRef.current = handleStop;
    }, [handleStop]);

    // null = idle at zone center; otherwise pad center in zone-local px
    const [padCenter, setPadCenter] = useState(null);
    const [stickOffset, setStickOffset] = useState({ x: 0, y: 0 });

    const reset = useCallback(() => {
        pointerIdRef.current = null;
        originRef.current = null;
        setPadCenter(null);
        setStickOffset({ x: 0, y: 0 });
        handleStopRef.current();
    }, []);

    // Size change invalidates origin coords — drop the drag.
    useEffect(() => {
        if (pointerIdRef.current !== null) reset();
    }, [size, reset]);

    const emitMove = useCallback(
        (relativeX, relativeY) => {
            const now = Date.now();
            if (throttle > 0 && now - lastMoveAtRef.current < throttle) {
                return;
            }
            lastMoveAtRef.current = now;
            const distance = Math.min(100, (Math.hypot(relativeX, relativeY) / radius) * 100);
            handleMoveRef.current({
                type: "move",
                x: (relativeX * 2) / baseSize,
                y: -((relativeY * 2) / baseSize),
                direction: null,
                distance,
            });
        },
        [throttle, radius, baseSize]
    );

    const onPointerDown = useCallback(
        (e) => {
            if (e.pointerType === "mouse" && e.button !== 0) {
                e.preventDefault();
                return;
            }
            if (pointerIdRef.current !== null) return;

            const zone = zoneRef.current;
            if (!zone) return;

            e.preventDefault();

            const rect = zone.getBoundingClientRect();
            let cx = e.clientX - rect.left;
            let cy = e.clientY - rect.top;
            // Keep the pad fully inside the hit zone
            cx = Math.max(radius, Math.min(rect.width - radius, cx));
            cy = Math.max(radius, Math.min(rect.height - radius, cy));

            pointerIdRef.current = e.pointerId;
            originRef.current = { cx, cy };
            setPadCenter({ cx, cy });
            setStickOffset({ x: 0, y: 0 });
            lastMoveAtRef.current = 0;

            zone.setPointerCapture(e.pointerId);
        },
        [radius]
    );

    const onPointerMove = useCallback(
        (e) => {
            if (pointerIdRef.current !== e.pointerId || !originRef.current) return;
            e.preventDefault();

            const zone = zoneRef.current;
            if (!zone) return;
            const rect = zone.getBoundingClientRect();
            const { cx, cy } = originRef.current;

            let relativeX = e.clientX - rect.left - cx;
            let relativeY = e.clientY - rect.top - cy;
            if (horizontalOnly) {
                relativeY = 0;
            }

            const dist = Math.hypot(relativeX, relativeY);
            if (dist > radius && dist > 0) {
                relativeX = (relativeX / dist) * radius;
                relativeY = (relativeY / dist) * radius;
            }

            setStickOffset({ x: relativeX, y: relativeY });
            emitMove(relativeX, relativeY);
        },
        [horizontalOnly, radius, emitMove]
    );

    const onPointerUp = useCallback(
        (e) => {
            if (pointerIdRef.current !== e.pointerId) return;
            reset();
        },
        [reset]
    );

    const onPointerCancel = useCallback(
        (e) => {
            if (pointerIdRef.current !== null && pointerIdRef.current !== e.pointerId) return;
            e.preventDefault();
            reset();
        },
        [reset]
    );

    // If the tab blurs mid-drag, pointerup may not fire — reset UI + stop.
    useEffect(() => {
        const onBlur = () => {
            if (pointerIdRef.current !== null) reset();
        };
        const onVisibility = () => {
            if (document.hidden && pointerIdRef.current !== null) reset();
        };
        window.addEventListener("blur", onBlur);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("blur", onBlur);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [reset]);

    const activeCenter = padCenter ?? { cx: homeCenter, cy: homeCenter };
    const decorationLeft = activeCenter.cx - size / 2;
    const decorationTop = activeCenter.cy - size / 2;
    const padLeft = activeCenter.cx - radius;
    const padTop = activeCenter.cy - radius;

    return (
        <div
            ref={zoneRef}
            className={`relative select-none ${className}`}
            style={{
                touchAction: "none",
                width: hitZoneSize,
                height: hitZoneSize,
            }}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onLostPointerCapture={() => {
                if (pointerIdRef.current !== null) reset();
            }}
        >
            {/* Arrow ring — follows snapped pad center */}
            <div
                className="pointer-events-none absolute rounded-full"
                style={{
                    width: size,
                    height: size,
                    left: decorationLeft,
                    top: decorationTop,
                }}
            >
                <div className="absolute grid h-full w-full grid-rows-3 grid-areas-joystick">
                    <div className="flex items-center justify-end grid-in-left">
                        <ArrowIcon className="rotate-180 h-6" />
                    </div>
                    <div className="flex items-center justify-center grid-in-middle">
                        <div
                            className="rounded-full bg-black60"
                            style={{
                                width: stickSize + 8,
                                height: stickSize + 8,
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-start grid-in-right">
                        <ArrowIcon className="h-6" />
                    </div>
                    {!horizontalOnly && (
                        <>
                            <div className="flex items-start justify-center grid-in-bottom pt-6">
                                <ArrowIcon className="rotate-90 h-6" />
                            </div>
                            <div className="flex items-end justify-center grid-in-top pb-6">
                                <ArrowIcon className="-rotate-90 h-6" />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Base pad */}
            <div
                className="pointer-events-none absolute rounded-full"
                style={{
                    width: baseSize,
                    height: baseSize,
                    left: padLeft,
                    top: padTop,
                    background: "rgba(51, 51, 51, 0.8)",
                }}
            />

            {/* Stick */}
            <div
                className="pointer-events-none absolute rounded-full bg-white"
                style={{
                    width: stickSize,
                    height: stickSize,
                    left: activeCenter.cx - stickSize / 2 + stickOffset.x,
                    top: activeCenter.cy - stickSize / 2 + stickOffset.y,
                }}
            />
        </div>
    );
};

export default JoystickWrapper;
